import React, { useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import PollLoadingView from '@components/common/PollLoadingView'
import { getPoll } from 'src/api/polls'
import QuestionPollSection from 'src/sections/QuestionPollSection'
import PollDetailSection from 'src/sections/PollDetailSection'
import { Poll } from 'src/api/types/polls'

type PollDetailViewProps = {
  pollId: number | undefined
  answerFocus?: boolean
  pollLoading?: boolean
  userId?: number
  activePollId?: number
  onPollDeleted: (pollId: number) => void
  onPlayPoll: (poll: Poll) => void
  onPollBack: () => void
  onBackToDetail?: () => void
  pollBackLabel?: string
  onNewPoll: () => void
  topRightLabel?: string
  onOpenHowToPlay: () => void
  sounds: {
    click: () => void
    submit: () => void
    drag: () => void
    celebrate: () => void
  }
}

export default function PollDetailView({
  pollId,
  answerFocus,
  pollLoading,
  userId,
  activePollId,
  onPollDeleted,
  onPlayPoll,
  onPollBack,
  onBackToDetail,
  pollBackLabel,
  onNewPoll,
  topRightLabel,
  onOpenHowToPlay,
  sounds,
}: PollDetailViewProps): JSX.Element | null {
  const [resource, setResource] = useState<Poll>()
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    let current = true
    setResource(undefined)
    setError('')
    if (!pollId || pollLoading) return
    getPoll(pollId)
      .then((result) => {
        if (!current) return
        if (result.success && result.data) setResource(result.data)
        else setError(result.message || 'Poll not found.')
      })
      .catch(() => {
        if (current) setError('Could not load this poll. Please try again.')
      })
    return () => {
      current = false
    }
  }, [pollId, userId, pollLoading, retry])
  if (!pollId && !pollLoading) return null

  if (error)
    return (
      <Box py={3}>
        <Typography>{error}</Typography>
        <Button
          onClick={() => {
            sounds.click()
            setRetry((value) => value + 1)
          }}
        >
          Retry
        </Button>
        <Button onClick={onPollBack}>Back to Polls</Button>
      </Box>
    )
  if (pollLoading || !resource || resource.id !== pollId)
    return (
      <PollLoadingView
        onBack={onPollBack}
        backLabel={pollBackLabel}
        onNewPoll={onNewPoll}
        statusLabel={topRightLabel}
      />
    )
  if (resource.pollFormat === 'community_answers')
    return (
      <QuestionPollSection
        key={pollId}
        poll={resource}
        userId={userId}
        onUpdated={setResource}
        answerFocus={answerFocus}
        onAnswerCancel={onBackToDetail}
        onBack={
          answerFocus && resource.viewerAnswer && onBackToDetail
            ? onBackToDetail
            : onPollBack
        }
        backLabel={answerFocus && resource.viewerAnswer ? 'Poll' : 'Polls'}
        onPlay={onPlayPoll}
        onDeleted={onPollDeleted}
        onOpenHowToPlay={onOpenHowToPlay}
        sounds={sounds}
      />
    )

  return (
    <PollDetailSection
      key={pollId}
      initialPoll={resource}
      pollId={pollId ?? 0}
      externalLoading={pollLoading}
      skipFetch={!pollId}
      userId={userId}
      onPollDeleted={(id) => {
        onPollDeleted(id)
      }}
      onPlayPoll={onPlayPoll}
      onPollBack={onPollBack}
      onBackToDetail={onBackToDetail}
      pollBackLabel={pollBackLabel}
      onNewPoll={onNewPoll}
      topRightLabel={topRightLabel}
      onOpenHowToPlay={onOpenHowToPlay}
      sounds={sounds}
    />
  )
}
